import React, { useEffect, useState } from "react";
import { useRole } from "../context/RoleContext";
import {
  listCityReports,
  reportCsvUrl,
  reportPdfUrl,
} from "../lib/cityPortal";
import { Download, FileText, ChevronDown, Eye } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";

/**
 * City Staff Reports Hub.
 *
 * Five canned, board-ready snapshots presented as scannable cards. Each card
 * leads with one prominent primary number, a row of 1-3 secondary numbers,
 * then the View / PDF / CSV actions. View toggles an inline table preview
 * inside the same card — no modal, no navigation.
 *
 * Decisions are governed by Accela; nothing on this surface writes back.
 */
export default function CityReports() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listCityReports(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  return (
    <section className="animate-fade-in-up" data-testid="city-reports-page">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          City Staff · Reports
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(26px, 3.6vw, 32px)",
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Reports.
      </h1>
      <p
        style={{
          color: "#6C6B85",
          fontSize: 14,
          marginTop: 4,
          maxWidth: 720,
          lineHeight: 1.55,
        }}
      >
        Observational snapshots of the WHOLE Neighborhoods platform —
        board-ready PDFs and CSVs you can hand to staff, council, or
        partners. Permitting decisions are governed by Accela; nothing on
        this page writes back to a case file.
      </p>

      {data?.generated_at && (
        <p
          className="mt-2 tabular-nums"
          style={{ fontSize: 12, color: "#6C6B85" }}
          data-testid="city-reports-generated-at"
        >
          Generated {relativeDate(data.generated_at)}.
        </p>
      )}

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 18 }} />

      {err && (
        <p
          style={{ color: "#A23B2E", marginTop: 16 }}
          data-testid="city-reports-error"
        >
          Could not load reports.
        </p>
      )}

      {!data && !err && (
        <div
          style={{ color: "#6C6B85", fontStyle: "italic", marginTop: 16 }}
          data-testid="city-reports-loading"
        >
          Loading reports…
        </div>
      )}

      {data?.items && (
        <div
          className="mt-6 grid gap-4"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
          data-testid="city-reports-list"
        >
          {data.items.map((r, i) => (
            <ReportCard
              key={r.id}
              report={r}
              userId={user.id}
              spanFull={i === data.items.length - 1 && data.items.length % 2 === 1}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Card                                                                    */
/* ────────────────────────────────────────────────────────────────────── */

function ReportCard({ report, userId, spanFull }) {
  const [expanded, setExpanded] = useState(false);
  const primary = report.summary?.primary;
  const secondary = report.summary?.secondary || [];

  return (
    <div
      className="wn-card"
      style={{
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gridColumn: spanFull ? "1 / -1" : undefined,
      }}
      data-testid={`city-reports-row-${report.id}`}
    >
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        {/* Title + blurb */}
        <div>
          <div className="flex items-center gap-2">
            <FileText size={15} style={{ color: "#1B1F4B" }} />
            <h3
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 17,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              {report.title}
            </h3>
          </div>
          <p
            style={{
              color: "#6C6B85",
              fontSize: 12.5,
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {report.blurb}
          </p>
        </div>

        {/* Primary + secondary metrics */}
        <div data-testid={`city-reports-metric-${report.id}`}>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span
              className="tabular-nums"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: 40,
                fontWeight: 600,
                color: "#1B1F4B",
                lineHeight: 1,
              }}
              data-testid={`city-reports-primary-value-${report.id}`}
            >
              {primary?.value ?? "—"}
            </span>
            <span
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 13,
                color: "#6C6B85",
                lineHeight: 1.3,
              }}
              data-testid={`city-reports-primary-label-${report.id}`}
            >
              {primary?.label || ""}
            </span>
          </div>
          {secondary.length > 0 && (
            <div
              className="mt-3 flex items-center flex-wrap"
              style={{
                gap: "8px 16px",
                paddingTop: 8,
                borderTop: "1px solid #E8E7EE",
              }}
              data-testid={`city-reports-secondary-${report.id}`}
            >
              {secondary.map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-baseline"
                  style={{ gap: 5, fontSize: 12, color: "#272636" }}
                >
                  <span
                    className="tabular-nums"
                    style={{
                      fontWeight: 700,
                      color: "#1B1F4B",
                      fontSize: 13,
                    }}
                  >
                    {s.value}
                  </span>
                  <span style={{ color: "#6C6B85" }}>{s.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Spacer to push actions to the bottom of equal-height cards */}
        <div style={{ flex: 1, minHeight: 6 }} aria-hidden />

        {/* Actions */}
        <div
          className="flex items-center justify-between gap-3 flex-wrap"
          style={{
            paddingTop: 10,
            borderTop: "1px solid #E8E7EE",
          }}
        >
          <span
            className="tabular-nums"
            style={{
              fontSize: 11,
              color: "#6C6B85",
              letterSpacing: "0.04em",
            }}
            data-testid={`city-reports-row-meta-${report.id}`}
          >
            {report.row_count}{" "}
            {report.row_count === 1 ? "row" : "rows"}
            {report.columns?.length
              ? ` · ${report.columns.length} columns`
              : ""}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="wn-btn wn-btn-outline inline-flex items-center"
              style={{ gap: 6, fontSize: 12 }}
              data-testid={`city-reports-view-${report.id}`}
              aria-expanded={expanded}
            >
              <Eye size={13} /> {expanded ? "Hide" : "View"}
              <ChevronDown
                size={13}
                style={{
                  transition: "transform 160ms ease",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>
            <a
              href={reportPdfUrl(report.id, userId)}
              className="wn-btn wn-btn-primary inline-flex items-center"
              style={{ gap: 6, fontSize: 12 }}
              data-testid={`city-reports-download-pdf-${report.id}`}
            >
              <Download size={13} /> PDF
            </a>
            <a
              href={reportCsvUrl(report.id, userId)}
              className="wn-btn wn-btn-tertiary inline-flex items-center"
              style={{ gap: 6, fontSize: 12 }}
              data-testid={`city-reports-download-csv-${report.id}`}
            >
              <Download size={13} /> CSV
            </a>
          </div>
        </div>
      </div>

      {expanded && <ReportPreview report={report} />}
    </div>
  );
}

function ReportPreview({ report }) {
  const rows = report.rows || [];
  const cols = report.columns || [];
  return (
    <div
      style={{
        borderTop: "1px solid #D9D8E4",
        background: "#FBFAFD",
      }}
      data-testid={`city-reports-preview-${report.id}`}
    >
      {rows.length === 0 ? (
        <p
          style={{
            padding: "16px 20px",
            margin: 0,
            color: "#6C6B85",
            fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
            fontSize: 13,
          }}
        >
          No data is available for this report at the current time.
        </p>
      ) : (
        <div style={{ overflow: "auto", maxHeight: 320 }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12.5,
              tableLayout: "auto",
            }}
            data-testid={`city-reports-table-${report.id}`}
          >
            <thead>
              <tr style={{ background: "#1B1F4B", color: "#FBFAFD" }}>
                {cols.map((c) => (
                  <th
                    key={c}
                    scope="col"
                    style={{
                      padding: "8px 14px",
                      textAlign: "left",
                      fontSize: 10,
                      letterSpacing: "0.06em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      position: "sticky",
                      top: 0,
                      borderRight: "1px solid rgba(251,250,253,0.12)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background: i % 2 === 0 ? "#FBFAFD" : "#F3F2F6",
                  }}
                  data-testid={`city-reports-table-row-${report.id}-${i}`}
                >
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      style={{
                        padding: "7px 14px",
                        color: "#272636",
                        verticalAlign: "top",
                        borderBottom: "1px solid #E8E7EE",
                        ...(j > 0 ? { textAlign: "right" } : {}),
                      }}
                      className={j > 0 ? "tabular-nums" : ""}
                    >
                      {cell == null || cell === "" ? (
                        <span style={{ color: "#9AA0C0" }}>—</span>
                      ) : (
                        String(cell)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
